import type { Agent } from "@/schemas/agent.schema";
import { onMounted, onUnmounted } from "vue";

type Listener<T extends unknown[]> = (...args: T) => void;

/**
 * @description
 * This is a type that maps events to their payloads.
 */
export type EventMap = {
  "thread-delete": [threadId: string];
  "thread-update": [threadId: string];
  "thread-create": [threadId: string];
  "tool-call": [{ id: string; content: string; escalation?: "answered" | "skipped" }];
  /** AI-1179: re-send the last user message (failed tool block "try again"). */
  "retry-thread": [];
  "agent-model-change": [model: string];
  "agent-updated": [Agent];
  "language-synced": [{ language: string }];
  logout: [];
};

export class EventEmitter<EventMap extends Record<string, unknown[]>> {
  private eventListeners: {
    [K in keyof EventMap]?: Set<Listener<EventMap[K]>>;
  } = {};

  on<K extends keyof EventMap>(event: K, listener: Listener<EventMap[K]>) {
    if (!this.eventListeners[event]) {
      this.eventListeners[event] = new Set();
    }
    this.eventListeners[event]?.add(listener);

    return () => {
      this.eventListeners[event]?.delete(listener);
    };
  }

  emit<K extends keyof EventMap>(event: K, ...args: EventMap[K]) {
    this.eventListeners[event]?.forEach((listener) => {
      listener(...args);
    });
  }

  /**
   * Removes ALL listeners of an event. Prefer the unsubscribe function returned
   * by `on` to remove a single listener; this clear-all is only for tearing down
   * an event wholesale.
   */
  off<K extends keyof EventMap>(event: K) {
    this.eventListeners[event]?.clear();
  }
}

export const emitter = new EventEmitter<EventMap>();

export function createEmitter<EventMap extends Record<string, unknown[]>>() {
  return new EventEmitter<EventMap>();
}

export function useEmitter<K extends keyof EventMap>(
  event: K | K[],
  listener: Listener<EventMap[K]>
) {
  // Track this component's own unsubscribe handles so cleanup removes only the
  // listeners it registered. Using emitter.off(event) on unmount would clear
  // every component's listener for that event.
  let unsubscribers: Array<() => void> = [];

  onMounted(() => {
    const events = Array.isArray(event) ? event : [event];
    unsubscribers = events.map((e) => emitter.on(e, listener));
  });

  onUnmounted(() => {
    unsubscribers.forEach((unsubscribe) => unsubscribe());
    unsubscribers = [];
  });
}
